// Import Packages
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Editor, EditorState, CompositeDecorator, ContentState, Modifier } from 'draft-js';
import { useSetRecoilState, useRecoilState } from "recoil";
import 'prismjs/components/prism-python';

// Import Material UI
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { IconButton } from '@material-ui/core';
import FilterCenterFocusIcon from '@material-ui/icons/FilterCenterFocus';

// Import Pages
import { VariableModal } from '../VariableModal/VariableModal';
import { lastFocusField, inputRefresh, showObjectExplorerIcon, activeAutom, selectedField } from "../../../recoil-store/recoil-state";
import ObjectExplorer from './components/ObjectExplorer/ObjectExplorer_new_design';
import RichTextEditor from "react-rte";

// Import CSS
import './TextInput.css'

/**
 * @description This function lets you return component according to the name of the incoming action.
 * @param       action - object
 * @param       inputName - string
 * @param       variables - object variable list
 * @param       multiline - boolean
 * @param       onBlur - function
 * @param       include - array
 * @param       disabled - boolean
 * @param       setOpenVariable - open variable function that allows us to open
 * @param       isRequired - boolean
 * @param       isCredentialPickerValue - boolean
 * @param       inputMode - string
 * @returns     Component
 * @author      ibrahim AKIN
 */

const styles = {
    handle: {
        color: '#000',
        fontWeight: 'bold',
        direction: 'ltr',
        unicodeBidi: 'bidi-override'
    }
};

const HANDLE_REGEX = /(##\w*(\[+\w+\]+)*##)/g;

function handleStrategy(contentBlock, callback) {
    findWithRegex(HANDLE_REGEX, contentBlock, callback);
}

function findWithRegex(regex, contentBlock, callback) {
    const text = contentBlock.getText();
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        callback(start, start + matchArr[0].length);
    }
}

const HandleSpan = (props) => {
    return (
        <span style={styles.handle} data-offset-key={props.offsetKey}>
            {props.children}
        </span>
    );
};

function TextInput({
    action,
    inputName,
    variables,
    multiline,
    onBlur,
    include,
    recorder,
    disabled,
    isRequired,
    setOpenVariable,
    inputMode = 'text',
    isVariableAdd = true,
    refresh = false,
    defaultValue = undefined,
    isCredentialPickerValue,
    setField,
    validation,
    fieldType,
    displayName,
    type
}) {
    const [inputRefreshGlobal] = useRecoilState(inputRefresh);
    const [showOEIcon, setShowOEIcon] = useRecoilState(showObjectExplorerIcon);
    const [automDetail, setAutomDetail] = useRecoilState(activeAutom);
    const [variableDesc, setVariableDesc] = useState(RichTextEditor.createEmptyValue());
    const [selectedFieldValue] = useRecoilState(selectedField);
    const [focusField, setFocusField] = useRecoilState(lastFocusField);

    const compositeDecorator = useRef(new CompositeDecorator([{
        strategy: handleStrategy,
        component: HandleSpan
    }]));
    const [open, setOpen] = useState(false);
    const [showObjectExporer, setShowObjectExplorer] = useState(false);

    const [dropdownValue, setDropdownValue] = useState({});
    const [indexValue, setIndexValue] = useState(0);
    const { t } = useTranslation();
    const editor = useRef(false);
    const validate = useRef(false);

    useEffect(() => {
        if (action?.fields && !action.fields[inputName]) {
            action.fields[inputName] = { isRequired, value: defaultValue ? defaultValue : '' };

            if (displayName) {
                action.fields[inputName].conditions = [];
            }
            if (setField) {
                // setField(inputName, action.fields[inputName]);
            }
        }
        setState(createState(getValue()));
        if (type === "html") {
            setVariableDesc(RichTextEditor.createValueFromString(action.fields[inputName].value, "html"));
        }
    }, [action, inputName, disabled, refresh, inputRefreshGlobal]);

    const getValue = () => {
        if (action) {
            if (action.fields && inputName) {
                return action.fields[inputName]?.value?.length ? action.fields[inputName].value : '';
            } else if (inputName || inputName >= 0) {
                return action[inputName]?.value ? action[inputName].value : '';
            } else {
                return action.value ? action.value : '';
            }
        } else {
            return '';
        }
    };

    const checkValidate = async (validateConfiguration) => {
        let isValidate = true;

        if (validateConfiguration.length) {
            validateConfiguration.forEach(item => {
                switch (item.type) {
                    case "length":
                        const newValue = state.editorState.getCurrentContent().getPlainText().length;
                        if (item.validation.operator === "lt") {
                            isValidate = newValue < item.validation.value;
                            break;
                        } else if (item.validation.operator === "gt") {
                            isValidate = newValue > item.validation.value;
                            break;
                        } else {
                            isValidate = newValue === item.validation.value;
                            break;
                        }
                    default:
                        isValidate = true;
                        break;
                }
            })
        }

        return isValidate;
    }

    // c => conditions
    const setValue = (v, c) => {
        if (action.fields && inputName) {
            // if (action.fields[inputName]) {
            //     action.fields[inputName].value = v;
            //     action.fields[inputName].isRequired = isRequired;

            // } else {
            //     action.fields[inputName] = { value: v, isRequired };
            // }
            action.fields[inputName] = { ...action.fields[inputName], value: v, isRequired };
            // if (c) {
            //     action.fields[inputName].conditions = c;
            //     const anyActionHasCondition = c.some(function (a) { return a.length });
            //     if (!anyActionHasCondition) {
            //         if (action.conditions) {
            //             const fieldIndex = action.conditions.indexOf(inputName);
            //             action.conditions.splice(fieldIndex, 1);
            //         }
            //     } else if (action.conditions) {
            //         if (!action.conditions.find(x => x === inputName)) {
            //             action.conditions.push(inputName);
            //         }
            //     } else {
            //         action.conditions = [inputName];
            //     }
            // }
            if (setField) {
                setField(inputName, action.fields[inputName]);
            }
        } else if (inputName || inputName >= 0) {
            action[inputName] = {
                ...action[inputName],
                value: v,
                isRequired
            };
        } else if (action) {
            action.value = v;
        }
    };

    const createState = (value) => {
        return ({
            editorState: EditorState.createWithContent(
                ContentState.createFromText(value),
                compositeDecorator.current
            )
        });
    };

    const [state, setState] = useState(createState(getValue()));

    const onChange = async (editorState) => {
        // const cv = await checkValidate(validation);
        // console.log("CV", cv)
        // if (cv) {
        setValue(editorState.getCurrentContent().getPlainText());
        setState({ editorState });
        // }
    };

    const onChangeRichText = async (v) => {
        // const cv = await checkValidate(validation);
        // console.log("CV", cv)
        // if (cv) {
        setVariableDesc(v);
        setValue(v.toString("html"));
        // }
    };

    const handleAddClick = () => {
        if (dropdownValue.type === 'array') {
            setState({ editorState: insertText('##' + dropdownValue.name + '[' + indexValue + ']##', state.editorState) });
        } else {
            setState({ editorState: insertText('##' + dropdownValue.name + '##', state.editorState) });
        }
        setOpen(false);
    };

    const insertText = (text, editorState) => {
        const currentContent = editorState.getCurrentContent(), currentSelection = editorState.getSelection();
        const newContent = Modifier.replaceText(currentContent, currentSelection, text);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
    };

    useEffect(() => {
        if (selectedFieldValue.path.length && focusField.inputName && focusField.inputName === inputName) {

            const valueEndIndex = selectedFieldValue.path.indexOf('[')
            let firstValue;
            let secondValue;
            if (valueEndIndex !== -1) {
                firstValue = selectedFieldValue.path.substring(2, valueEndIndex)
                secondValue = selectedFieldValue.path.substring(valueEndIndex)
            } else {
                firstValue = selectedFieldValue.path.substring(2, selectedFieldValue.path.length - 2)
                secondValue = selectedFieldValue.path.substring(selectedFieldValue.path.length - 2);
            }
            let v;
            if (selectedFieldValue.key === '') {
                v = `##${firstValue}${secondValue}`;
            } else {
                v = `##${selectedFieldValue.key}[${firstValue}]${secondValue}`;
            }
            setState({ editorState: insertText(v, state.editorState) });
            // if (!focusField.is_sys_field) {

            //     // setValue(editorState.getCurrentContent().getPlainText());
            //     // if (props.selectedAction.fields[focusField.inputName].value) {
            //     //     props.selectedAction.fields[focusField.inputName].value += v;
            //     // } else {
            //     //     props.selectedAction.fields[focusField.inputName].value = v;
            //     // }

            // } else {
            //     // props.selectedAction.fields[sysActionFielName].value[focusField.inputName].value = v;
            // }
            // setInputRefreshGlobal(new Date());
        }
    }, [selectedFieldValue])


    const handleClickOpen = () => {
        setOpen(true);
    };

    const requireValidate = (!getValue() && isRequired) && !isCredentialPickerValue;
    const errorValidate = inputMode === 'text' ? requireValidate : requireValidate || !validate.current;

    const handleOnFocus = () => {
        setFocusField({
            inputName,
            type: include && include.length ? include[0] : '',
            //is_sys_field: !action.fields
        });
    }

    const handleShowObjectExplorer = () => {
        if (showOEIcon) {
            setShowOEIcon(false);
        }
        setShowObjectExplorer(true);
    }

    const cancelObjectExplorer = () => {
        setShowOEIcon(true);
        setShowObjectExplorer(false);
    }

    const saveObjectExplorer = (f, conditionExist) => {
        if (action.fields && inputName) {
            action.fields[inputName].functions = f;
        }

        //     action.fields[inputName].conditions = c;
        //    // const anyActionHasCondition = c.some(function (a) { return a.length });
        if (!conditionExist) {
            if (action.conditions) {
                const fieldIndex = action.conditions.indexOf(inputName);
                action.conditions.splice(fieldIndex, 1);
            }
        } else if (action.conditions) {
            if (!action.conditions.find(x => x === inputName)) {
                action.conditions.push(inputName);
            }
        } else {
            action.conditions = [inputName];
        }


        //setValue(data.value, data.conditions)
        // EditorState.set(editorState, data.value);
        //setState(createState(data.value)); // insertText(data.value, state.editorState)
        setShowOEIcon(true);
        setShowObjectExplorer(false);
        let copyItems = JSON.parse(JSON.stringify(automDetail.items))
        findAndUpdateItem(copyItems)
        setAutomDetail({
            ...automDetail,
            items: copyItems
        })
    }

    const findAndUpdateItem = (items) => {
        items.forEach(item => {
            if (item.item_id === action.item_id) {
                item.fields = JSON.parse(JSON.stringify(action.fields))
                item.conditions = JSON.parse(JSON.stringify(action.conditions))
                return;
            } else if (item.children) {
                findAndUpdateItem(item.children)
            }
        })
    }

    const saveNewValue = (v, selectedVariableName) => {

        if (action.fields && inputName) {
            action.fields[inputName] = { ...action.fields[inputName], value: v, selectedVariableName, isRequired };
            if (setField) {
                setField(inputName, action.fields[inputName]);
            }
        }
        setState(createState(v));
        setShowOEIcon(true);
        setShowObjectExplorer(false);
    }

    return (
        <>
            {requireValidate && inputMode === 'text' &&
                <small style={{ color: '#D75E5E' }}>&nbsp; {t('FLOW-MESSAGES.LIBRARY.GENERAL.REQUIRED')}</small>}
            <div className='new-text-input-container'>
                <div className={`editable${multiline ? ' multiline' : ''}`}
                    style={{
                        minHeight: multiline && 100,
                        border: errorValidate && '1px solid #E6E6E6',
                        backgroundColor: "#FCFCFC"
                    }}
                >
                    {type === "html" ? (
                        <RichTextEditor
                            value={variableDesc}
                            editorClassName="publish-editor"
                            onChange={value => onChangeRichText(value)}
                        // toolbarConfig={toolbarConfig}
                        />
                    ) : (
                        <Editor onFocus={handleOnFocus} editorState={state.editorState}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={editor}
                            readOnly={recorder || disabled} style={{ minHeight: multiline && 100 }} />
                    )}
                </div>
                {(showOEIcon && fieldType && (fieldType.includes('string') || state.editorState.getCurrentContent().getPlainText())) &&
                    <IconButton className="object-explorer-button" style={{ position: 'absolute', float: 'rigth' }}
                        onClick={handleShowObjectExplorer}>
                        <FilterCenterFocusIcon style={{ paddingTop: 5, color: "#8C90A2" }} />
                    </IconButton>
                }
                {isVariableAdd &&
                    <IconButton className="add-variable-button" onClick={handleClickOpen}
                        disabled={disabled}>
                        <AddCircleOutlineIcon className="add-variable-icon" />
                    </IconButton>}
                {open && (
                    <VariableModal setOpen={setOpen} variables={variables}
                        dropdownValue={dropdownValue} setDropdownValue={setDropdownValue}
                        setOpenVariable={setOpenVariable}
                        indexValue={indexValue} setIndexValue={setIndexValue}
                        handleAddClick={handleAddClick} include={include}
                    />
                )}
            </div>
            {
                (requireValidate || !validate.current) && inputMode === 'email' &&
                <div><small style={{ color: '#D75E5E' }}><em> * Enter a valid email address.</em></small></div>
            }
            {
                showObjectExporer && (
                    <ObjectExplorer
                        show={showObjectExporer} cancel={cancelObjectExplorer}
                        variables={variables} setOpenVariable={setOpenVariable}
                        field={{
                            value: state.editorState.getCurrentContent().getPlainText(), name: inputName,
                            type: fieldType, displayName,
                            selectedVariableName: action.fields && action.fields[inputName] ? action.fields[inputName].selectedVariableName : ''
                        }}
                        functions={
                            action.fields && action.fields[inputName]
                                && action.fields[inputName].functions && action.fields[inputName].functions.length
                                ? action.fields[inputName].functions : []
                        }
                        save={saveObjectExplorer}
                        saveNewValue={saveNewValue}
                    // fieldConditions={
                    //     action.fields && action.fields[inputName]
                    //         && action.fields[inputName].conditions && action.fields[inputName].conditions.length
                    //         ? action.fields[inputName].conditions : []
                    // }
                    // selectedVariable={action.fields[inputName]} saveConfiguration={() => { }}
                    //value={assignValues[configureModal.index]} variableRender={variableRender} 
                    />
                )
            }
        </>
    );
}

export { TextInput };
